import bmLogoWhite from "@img/logos/bankmonitor-white.svg";
import { FeatureFlag } from "@utils/FeatureFlag";
import { currentYear } from "@utils/current-year";
import { ResponsiveHelper } from "@utils/responsive-helper";
import { PromisePool, addParameterToURL } from "bm-ui-kit";
import type { ReactNode } from "react";
import * as React from "react";
import { Accordion, Container } from "react-bootstrap";
import { FooterFeaturesHandler } from "../FooterFeaturesHandler/FooterFeaturesHandler";
import { SocialIcons } from "../SocialIcons/SocialIcons";
import { FOOTER_MENU_BLOCKS, FOOTER_MENU_ROW } from "./data";
import type { Variant } from "./type";
import "./FooterMenu.scss";

interface Props {
	variant: Variant;
}

interface State {
	isMobile: boolean;
	loaded: boolean;
}

/**
 * @Shortcode
 *
 * @title "Footer Menü"
 * @shortcode "footer_menu"
 * @type "widget"
 * @propsList "[{"variant":"retail"},{"variant":"corporate"}]"
 * @importMode "eager"
 */
export default class FooterMenu extends React.Component<Props, State> {
	private responsiveHelper = new ResponsiveHelper("lg", (isOver) => {
		this.setState({
			isMobile: !isOver,
		});
	});
	private readonly promisePool = new PromisePool();

	public constructor(props: Props) {
		super(props);

		this.state = {
			isMobile: !this.responsiveHelper.getActualStatus(),
			loaded: false,
		};
	}

	public componentDidMount(): void {
		this.responsiveHelper.startObserving();

		this.setState({
			loaded: true,
		});
	}

	public componentWillUnmount(): void {
		this.responsiveHelper.stopObserving();
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { variant } = this.props;
		const { loaded } = this.state;

		return (
			<>
				{loaded && <FooterFeaturesHandler variant={variant} />}
				<footer className="main-footer pt-2 text-white">
					<Container>
						{this.renderSocialsRow()}
						{this.renderTopLinkRow()}
						{this.renderProductsAndServices()}
						{this.renderWhoAsksUs()}
						{this.renderPartners()}
					</Container>
					<Container className="copyright py-2 px-0 text-center border-top" fluid>
						Copyright {currentYear()} Bankmonitor
					</Container>
				</footer>
			</>
		);
	}

	private renderSocialsRow(): ReactNode {
		return this.renderBottomTitle(
			<div className="d-flex mb-2 align-items-center justify-content-center justify-content-md-between">
				<img className="d-none d-sm-block" src={bmLogoWhite} alt="Bankmonitor" height={30} />
				<div>
					<SocialIcons colorscheme="dark" />
				</div>
			</div>,
		);
	}

	private renderTopLinkRow(): ReactNode {
		return (
			<ul className="text-xl-center mb-3 pb-2 list-inline footer-menu">
				{FOOTER_MENU_ROW.map((item) => (
					<li key={item.url} className="mx-0 my-0 px-2 py-0 ml-2 ml-md-0 d-md-inline-block">
						<a
							className="text-white fs-sm"
							href={addParameterToURL(item.url, { link_type: "footer-menu-row-item" })}
						>
							{item.menuPoint}
						</a>
					</li>
				))}
			</ul>
		);
	}

	private renderProductsAndServices(): ReactNode {
		const { isMobile } = this.state;

		return (
			<div className="footer-termek-menu p-0 mb-4">
				{this.renderBottomTitle("Termékek és szolgáltatások")}
				<Accordion activeKey={isMobile ? undefined : "allGroups"}>
					<ul className="p-0 d-inline fs-sm">
						{FOOTER_MENU_BLOCKS.map((item) => (
							<li
								className="col-md-6 col-lg-4 col-xl-3 group d-inline-block mb-3 pb-1"
								key={item.groupId + item.groupTitle}
							>
								<Accordion.Toggle as="span" eventKey={item.groupId} className="h4 group-title">
									{item.groupTitle}
								</Accordion.Toggle>
								<Accordion.Collapse eventKey={isMobile ? item.groupId : "allGroups"} className="pt-1">
									<ul className="pl-0">
										{item.groupItems.map((menuItem) => (
											<li className="py-1" key={menuItem.url}>
												<a
													href={addParameterToURL(menuItem.url, {
														link_type: "footer-menu-item",
													})}
												>
													{menuItem.menuPoint}
												</a>
											</li>
										))}
									</ul>
								</Accordion.Collapse>
							</li>
						))}
					</ul>
				</Accordion>
			</div>
		);
	}

	private renderWhoAsksUs(): ReactNode {
		return (
			<div>
				{this.renderBottomTitle("Akik minket kérdeznek")}
				<div className="logos mb-3 pb-1">
					<a href="/akik-minket-kerdeznek/" target="_blank" rel="noreferrer">
						<ul className="logo-list list-unstyled">
							<li className="rtl" aria-label="RTL" />
							<li className="tv2" aria-label="TV2" />
							<li className="hvg" aria-label="HVG" />
							<li className="hu24" aria-label="24.hu" />
							<li className="pp" aria-label="Piac és Profit" />
							<li className="millas" aria-label="Millásreggeli" />
							<li className="index" aria-label="index.hu" />
							<li className="napi" aria-label="napi.hu" />
							<li className="kamara" aria-label="Kamara online" />
						</ul>
					</a>
				</div>
			</div>
		);
	}

	private renderPartners(): ReactNode {
		return (
			<div>
				{this.renderBottomTitle("Partnereink")}
				<div className="logos mb-3">
					<ul className="logo-list list-unstyled">
						<li className="jooble" aria-label="Jooble">
							<a href="/akik-minket-kerdeznek/" target="_blank" rel="noreferrer">
								Jooble
							</a>
						</li>
						<li className="tc" aria-label="TC2">
							<a href="/akik-minket-kerdeznek/" target="_blank" rel="noreferrer">
								TC2
							</a>
						</li>

						<li className="aws" aria-label="AWS">
							<a href="/akik-minket-kerdeznek/" target="_blank" rel="noreferrer">
								AWS
							</a>
						</li>

						<li className="portfolio">
							<a href="/akik-minket-kerdeznek/" target="_blank" rel="noreferrer">
								Portfólió
							</a>
						</li>

						<li className="barion" aria-label="Barion">
							<a href="https://www.barion.com/hu/maganszemelyek/dijak/" target="_blank" rel="noreferrer">
								Barion
							</a>
						</li>
					</ul>
				</div>
				<FeatureFlag flag="mock-feature">
					<strong data-testid="mock_feature">This is a mock feature</strong>
				</FeatureFlag>
			</div>
		);
	}

	private renderBottomTitle(title: ReactNode): ReactNode {
		return <div className="h5 head-text text-uppercase font-weight-light mb-3 pb-1 border-bottom">{title}</div>;
	}
}
